import { v4 as uuid } from 'uuid'
import * as Sentry from '@sentry/browser'
import { session } from '@/composables/useSession'
import { timeTrackingApi as api } from '@/api'

import { type PauseParams, type StopParams, type StopResponse, TimestampType } from './types'

const LIMIT_PAUSE_10_MINUTES = 10 * 60
const LIMIT_PAUSE_15_MINUTES = 15 * 60
const LIMIT_PAUSE_30_MINUTES = 30 * 60

let externalId: string | undefined
let connectionId: number | undefined
let isCalibration: boolean | undefined
let eventListenersAdded: boolean

const setId = (ticketIdentifier: { externalId: string; connectionId: number; isCalibration?: boolean }) => {
  externalId = ticketIdentifier.externalId
  connectionId = ticketIdentifier.connectionId
  isCalibration = !!ticketIdentifier.isCalibration
}

const clearId = () => {
  externalId = undefined
  connectionId = undefined
  isCalibration = undefined
}

const visibilityListener = () => {
  if (document.visibilityState === 'visible') start()
  else pause({ limit: true })
}

const addListeners = () => {
  if (eventListenersAdded) return
  document.addEventListener('visibilitychange', visibilityListener)
  eventListenersAdded = true
}

const sendTimestamp = async (type: TimestampType, params: StopParams | PauseParams = {}) => {
  if (!externalId || !connectionId) return

  const body = {
    id: uuid(),
    type,
    conversationId: externalId,
    connectionId,
    isCalibration,
    ...params,
  }

  try {
    return await api.post('t', {
      json: body,
      keepalive: true,
      headers: { 'X-Klaus-Workspace': session.workspace.id.toString() },
    })
  } catch (e) {
    console.info('Time tracking event failed to send', e)

    // TODO: Maybe remove this after debugging of time tracking issues
    Sentry.captureException(e, {
      tags: {
        component: 'time-tracking',
        endpoint: '/api/t',
        timestamp_type: type,
      },
      extra: {
        requestBody: body,
        workspaceId: session.workspace.id,
        externalId,
        connectionId,
        isCalibration,
        params,
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      contexts: {
        timeTracking: {
          type,
          conversationId: externalId,
          connectionId,
          isCalibration,
          limitPause: 'limitPause' in params ? params.limitPause : undefined,
        },
        session: {
          workspaceId: session.workspace.id,
          features: {
            limitPauseInTimeTrackingFor10Min: session.features.limitPauseInTimeTrackingFor10Min,
            limitPauseInTimeTrackingFor15Min: session.features.limitPauseInTimeTrackingFor15Min,
            limitPauseInTimeTracking: session.features.limitPauseInTimeTracking,
          },
        },
      },
    })
  }
}

const start = () => {
  addListeners()
  if (session.features.limitPauseInTimeTrackingFor10Min) {
    sendTimestamp(TimestampType.Start, { limitPause: LIMIT_PAUSE_10_MINUTES })
    return
  }

  if (session.features.limitPauseInTimeTrackingFor15Min) {
    sendTimestamp(TimestampType.Start, { limitPause: LIMIT_PAUSE_15_MINUTES })
    return
  }

  if (session.features.limitPauseInTimeTracking) {
    sendTimestamp(TimestampType.Start, { limitPause: LIMIT_PAUSE_30_MINUTES })
    return
  }

  sendTimestamp(TimestampType.Start)
}

const pause = ({ limit } = { limit: false }) => {
  if (session.features.limitPauseInTimeTrackingFor10Min && limit) {
    return sendTimestamp(TimestampType.Pause, { limitPause: LIMIT_PAUSE_10_MINUTES })
  }

  if (session.features.limitPauseInTimeTrackingFor15Min && limit) {
    return sendTimestamp(TimestampType.Pause, { limitPause: LIMIT_PAUSE_15_MINUTES })
  }

  if (session.features.limitPauseInTimeTracking && limit) {
    return sendTimestamp(TimestampType.Pause, { limitPause: LIMIT_PAUSE_30_MINUTES })
  }

  return sendTimestamp(TimestampType.Pause)
}

const stop = async (params: StopParams): Promise<StopResponse> => {
  const response = await sendTimestamp(TimestampType.Stop, params)
  start()
  if (!response) return { reviewTime: null }
  try {
    return await response.json<StopResponse>()
  } catch {
    return { reviewTime: null }
  }
}

const removeListeners = () => {
  document.removeEventListener('visibilitychange', visibilityListener)
  eventListenersAdded = false
}

export default {
  setId,
  clearId,
  start,
  pause,
  stop,
  removeListeners,
}
